import React, { useState } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { teamService } from '../../services/api';

const InvitePartner: React.FC = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [tradeType, setTradeType] = useState('gas_safety');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await teamService.invitePartner({
                email,
                name,
                tradeType,
                message
            });
            setSuccess(true);
            setEmail('');
            setName('');
            setMessage('');
        } catch (err: any) {
            console.error('Failed to invite partner:', err);
            setError(err.response?.data?.error || err.message || 'Failed to send invitation');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <Navbar />

            <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-12">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-green-600 to-teal-600 p-8 text-white text-center">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/30">
                            <i className="fas fa-handshake text-3xl"></i>
                        </div>
                        <h1 className="text-3xl font-bold">Invite a Trade Partner</h1>
                        <p className="mt-2 text-green-50 font-medium">Join forces with verified professionals to maintain your properties</p>
                    </div>

                    <div className="p-8 md:p-12">
                        {success ? (
                            <div className="text-center py-10 animate-in fade-in zoom-in duration-500">
                                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <i className="fas fa-check text-2xl"></i>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitation Sent!</h2>
                                <p className="text-gray-600 mb-8">We've sent an invitation to join the Landlord Pro network. They can register and begin accepting jobs from you once verified.</p>
                                <button
                                    onClick={() => setSuccess(false)}
                                    className="px-8 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition shadow-lg"
                                >
                                    INVITE ANOTHER
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                                        <div className="flex">
                                            <i className="fas fa-exclamation-circle text-red-500 mt-0.5 mr-3"></i>
                                            <p className="text-sm text-red-700">{error}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Partner Name / Business</label>
                                        <input
                                            type="text"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="e.g. John's Plumbing"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all outline-none text-gray-800"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="partner@example.com"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all outline-none text-gray-800"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Trade Specialization</label>
                                    <select
                                        value={tradeType}
                                        onChange={(e) => setTradeType(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all outline-none text-gray-800 appearance-none bg-white"
                                    >
                                        <option value="gas_safety">Gas Safety Engineer</option>
                                        <option value="electrician">Electrician (EICR)</option>
                                        <option value="epc_assessor">EPC Assessor</option>
                                        <option value="fire_safety">Fire Safety Specialist</option>
                                        <option value="handyman">General Maintenance / Handyman</option>
                                        <option value="cleaning">End of Tenancy Cleaning</option>
                                        <option value="inventory_clerk">Inventory Clerk</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Personal Message (Optional)</label>
                                    <textarea
                                        rows={4}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Add a friendly note to your invitation..."
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all outline-none text-gray-800 resize-none"
                                    ></textarea>
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full py-4 rounded-2xl text-white font-black text-lg shadow-xl shadow-green-200 transition-all active:scale-[0.98] ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center">
                                                <i className="fas fa-circle-notch fa-spin mr-2"></i> SENDING...
                                            </span>
                                        ) : 'SEND INVITATION'}
                                    </button>
                                </div>
                                <p className="text-center text-xs text-gray-500 mt-4">
                                    By inviting a partner, you agree they will be notified via email to join the Landlord Pro platform.
                                </p>
                            </form>
                        )}
                    </div>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                    <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <i className="fas fa-shield-alt text-green-500 text-xl mb-3"></i>
                        <h4 className="font-bold text-gray-900 mb-1">Vetted Professionals</h4>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-tighter">Automatic compliance verification</p>
                    </div>
                    <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <i className="fas fa-bolt text-amber-500 text-xl mb-3"></i>
                        <h4 className="font-bold text-gray-900 mb-1">Fast Execution</h4>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-tighter">Assign jobs in one click</p>
                    </div>
                    <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <i className="fas fa-credit-card text-blue-500 text-xl mb-3"></i>
                        <h4 className="font-bold text-gray-900 mb-1">Secure Payments</h4>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-tighter">Integrated escrow-style protection</p>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default InvitePartner;
